import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { threadCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { Chip } from '@/ui/Chip/Chip'
import { Disclosure } from '@/ui/Disclosure/Disclosure'
import { TextareaField, TextField } from '@/ui/Field/Field'
import { Link } from '@/ui/Link/Link'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { Markdown } from '@/ui/Message/Markdown'
import { StatusBadge, type StatusTone } from '@/ui/Status/Status'
import { ApiError } from '../../api/client'
import { resourcesFor } from '../../api/resources'
import { plansApi, questionsApi } from '../../api/topics'
import { kindDisplay } from '../../constants/resourceKinds'
import { ROUTES } from '../../constants/routes'
import { useResourceKinds } from '../../hooks/useResourceKinds'
import { useToastStore } from '../../store/toast'
import type { TopicResource } from '../../types/api'
import { RefreshFailed } from '../RefreshFailed'
import styles from './ResourceCard.module.css'

/**
 * Inline card for a resource born in this thread (plan, question) — the
 * redesign §4 spec: kind chip + title + status in the header, a
 * collapsible body, `approve` / `request changes` while pending, and an
 * `open` link to the resource's page. The message carrying it is only
 * a pointer — the card body renders from a live resource fetch, so
 * decisions flip every card and the Resources page with no message
 * rewriting.
 *
 * Decisions apply optimistically: the card flips to its terminal state
 * immediately (thread status and the waiting-on-you count refresh via
 * invalidation) and rolls back to the fetched truth on failure.
 */

type ReviewAction = 'approve' | 'reject' | 'answer' | null

export function ResourceCard({
  topicId,
  resourceId,
  fallbackTitle,
}: {
  topicId: string
  resourceId: string
  /** From the pointer message — shown while the resource loads. */
  fallbackTitle?: string
}) {
  const qc = useQueryClient()
  const addToast = useToastStore((s) => s.addToast)
  const [annotations, setAnnotations] = useState('')
  const [freetext, setFreetext] = useState('')
  const [busy, setBusy] = useState<ReviewAction>(null)
  // Which option fired the pending answer: only that button shows it.
  const [busyOption, setBusyOption] = useState<string | null>(null)
  const { data: kinds } = useResourceKinds()
  const freetextId = useId()
  const annotationsId = useId()

  const cardKey = ['resource', 'card', topicId, resourceId]
  const {
    data: resource,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: cardKey,
    queryFn: () => resourcesFor({ kind: 'topic', id: topicId }).get(resourceId),
    staleTime: 5_000,
    retry: false,
  })

  const showSkeleton = useSkeletonDelay(!resource && !isError)
  const isNotFound = error instanceof ApiError && error.status === 404

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['resource'] })
    void qc.invalidateQueries({ queryKey: ['resources'] })
    void qc.invalidateQueries({ queryKey: ['waiting'] })
    void qc.invalidateQueries({ queryKey: ['threads', topicId] })
  }

  /** Optimistically stamp the card's review status; a refetch is the
   * rollback path on failure. */
  function applyOptimisticStatus(next: string) {
    qc.setQueryData<TopicResource>(cardKey, (old) =>
      old
        ? ({ ...old, data: { ...old.data, status: next } } as TopicResource)
        : old,
    )
  }

  async function run(
    action: NonNullable<ReviewAction>,
    fn: () => Promise<unknown>,
    optimisticStatus?: string,
  ) {
    setBusy(action)
    if (optimisticStatus) applyOptimisticStatus(optimisticStatus)
    try {
      await fn()
      setAnnotations('')
      setFreetext('')
      refresh()
    } catch {
      // Roll back to the server truth; the optimistic flip was cosmetic.
      void qc.invalidateQueries({ queryKey: cardKey })
      addToast(threadCopy.review.failed[action], 'error')
    } finally {
      setBusy(null)
      setBusyOption(null)
    }
  }

  if (isError && (!resource || isNotFound)) {
    if (isNotFound) {
      return (
        <Card>
          <Inline gap="space-8">
            <Text tone="text-sub">{fallbackTitle ?? resourceId}</Text>
            <Text variant="caption" tone="text-mute">
              {threadCopy.review.deleted}
            </Text>
          </Inline>
        </Card>
      )
    }
    return (
      <Alert tone="danger">
        <Inline gap="space-8" align="baseline">
          {threadCopy.view.resourceFailed}
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            {threadCopy.message.retry}
          </Button>
        </Inline>
      </Alert>
    )
  }

  if (!resource) {
    return (
      <Card aria-busy="true">
        <Stack gap="space-8">
          <Text tone="text-sub">{fallbackTitle ?? resourceId}</Text>
          {showSkeleton && <Skeleton lines={2} ground="surface" />}
        </Stack>
      </Card>
    )
  }

  // Narrow the resource union once: only plan/question carry review state.
  const planData = resource.kind === 'plan' ? resource.data : undefined
  const questionData = resource.kind === 'question' ? resource.data : undefined
  const reviewData = planData ?? questionData
  const status = String(reviewData?.status ?? 'submitted')
  const pending = status === 'submitted'

  const body = planData?.plan_text ?? questionData?.question
  const options = questionData?.options
  const kind = kindDisplay(resource.kind, kinds)

  function answerFreetext() {
    if (!resource || !freetext.trim() || busy !== null) return
    void run(
      'answer',
      () =>
        questionsApi.answer(
          topicId,
          resource.id,
          freetext.trim(),
          annotations || undefined,
        ),
      'answered',
    )
  }

  const card = (
    <Card>
      <Stack gap="space-8">
        <Inline gap="space-8">
          <Chip variant="kind" icon={kind.icon} label={kind.label} />
          <Text tone="text" className={styles.grow}>
            {resource.name}
          </Text>
          <StatusBadge tone={statusTone(status)}>
            {statusLabel(status, !!planData)}
          </StatusBadge>
          <Link href={ROUTES.resourceDetail(resource.id)}>
            {threadCopy.review.open}
          </Link>
        </Inline>

        {body && (
          <Disclosure
            label={threadCopy.review.contents}
            defaultOpen={pending}
            ground="surface"
          >
            <Markdown source={body} ground="surface" />
          </Disclosure>
        )}

        {options && options.length > 0 && (
          <Inline gap="space-8">
            {pending
              ? options.map((o) => (
                  <Button
                    key={o.value}
                    variant="outline"
                    ground="surface"
                    loading={busy === 'answer' && busyOption === o.value}
                    onClick={() => {
                      setBusyOption(o.value)
                      void run(
                        'answer',
                        () =>
                          questionsApi.answer(
                            topicId,
                            resource.id,
                            o.value,
                            annotations || undefined,
                          ),
                        'answered',
                      )
                    }}
                  >
                    {o.label}
                  </Button>
                ))
              : options.map((o) => (
                  <Chip key={o.value} variant="tag" label={o.label} />
                ))}
          </Inline>
        )}

        {questionData && pending && (!options || options.length === 0) && (
          <Inline gap="space-8" align="end">
            <TextField
              id={freetextId}
              label={threadCopy.review.answer}
              value={freetext}
              onValue={setFreetext}
              inputProps={{
                onKeyDown: (e) => {
                  if (e.key === 'Enter') answerFreetext()
                },
              }}
            />
            <Button
              variant="outline"
              ground="surface"
              disabled={!freetext.trim()}
              loading={busy === 'answer'}
              onClick={answerFreetext}
            >
              {threadCopy.review.answer}
            </Button>
          </Inline>
        )}

        {pending && (
          <>
            <TextareaField
              id={annotationsId}
              label={
                planData
                  ? threadCopy.review.noteForAgent
                  : threadCopy.review.notes
              }
              hint={threadCopy.review.optional}
              value={annotations}
              onValue={setAnnotations}
              textareaProps={{ rows: 2 }}
            />
            <Inline gap="space-8" justify="end">
              {planData && (
                <>
                  <Button
                    variant="outline"
                    ground="surface"
                    loading={busy === 'reject'}
                    onClick={() =>
                      void run(
                        'reject',
                        () =>
                          plansApi.reject(
                            topicId,
                            resource.id,
                            annotations || undefined,
                          ),
                        'rejected',
                      )
                    }
                  >
                    {threadCopy.review.requestChanges}
                  </Button>
                  <Button
                    variant="primary"
                    ground="surface"
                    loading={busy === 'approve'}
                    onClick={() =>
                      void run(
                        'approve',
                        () =>
                          plansApi.approve(
                            topicId,
                            resource.id,
                            annotations || undefined,
                          ),
                        'approved',
                      )
                    }
                  >
                    {threadCopy.review.approve}
                  </Button>
                </>
              )}
            </Inline>
          </>
        )}

        {!pending && reviewData && (
          <DecisionFooter
            data={reviewData}
            agentName={resource.author?.display_name}
          />
        )}
      </Stack>
    </Card>
  )

  // Contract B (D153): a failed refetch keeps the loaded card under a notice.
  if (isError) {
    return (
      <>
        <RefreshFailed
          message={threadCopy.view.resourceFailed}
          onRetry={() => void refetch()}
        />
        {card}
      </>
    )
  }
  return card
}

interface ReviewOutcomeData {
  status?: string
  decided_at?: number
  decided_by?: string
  answered_at?: number
  answered_by?: string
  annotations?: string | null
  answer?: string
}

function DecisionFooter({
  data,
  agentName,
}: {
  data: ReviewOutcomeData
  agentName?: string
}) {
  const status = String(data.status ?? 'submitted')
  const at = data.decided_at ?? data.answered_at
  const by = data.decided_by ?? data.answered_by
  const annotations = data.annotations ?? null
  const answer = data.answer

  const decisionLine =
    by && status === 'approved'
      ? threadCopy.review.approvedBy(by)
      : by && status === 'answered'
        ? threadCopy.review.answeredBy(by)
        : by
          ? threadCopy.review.changesRequestedBy(by)
          : ''

  return (
    <Stack gap="space-8">
      {answer !== undefined && answer !== null && (
        <Text as="p" variant="caption" tone="text-mute">
          {threadCopy.review.answer}{' '}
          <Text as="span" variant="caption" tone="text">
            {String(answer)}
          </Text>
        </Text>
      )}
      <Text as="p" variant="caption" tone="text-mute">
        {status === 'expired'
          ? threadCopy.review.expired
          : threadCopy.review.decision(
              (threadCopy.review.outcome as Record<string, string>)[status] ??
                status,
              by,
              at ? fmtTime(at) : undefined,
            )}
      </Text>
      {annotations && (
        <Text as="p" variant="caption" tone="text-mute">
          “{annotations}”
        </Text>
      )}
      {/* System line (§4 approve flow): who decided, and that the blocked
          agent is resuming. Derived from the outcome + author provenance. */}
      {(status === 'approved' ||
        status === 'answered' ||
        status === 'rejected') &&
        by && (
          <Text as="p" variant="caption" tone="text-mute">
            {agentName
              ? threadCopy.review.resuming(decisionLine, agentName)
              : decisionLine}
          </Text>
        )}
    </Stack>
  )
}

function statusTone(status: string): StatusTone {
  switch (status) {
    case 'submitted':
    case 'rejected':
      return 'warning'
    case 'approved':
    case 'answered':
      return 'success'
    default:
      return 'neutral'
  }
}

function statusLabel(status: string, isPlan: boolean): string {
  switch (status) {
    case 'submitted':
      return isPlan
        ? threadCopy.review.status.needsApproval
        : threadCopy.review.status.awaitingAnswer
    case 'approved':
      return threadCopy.review.status.approved
    case 'rejected':
      return threadCopy.review.status.changesRequested
    case 'answered':
      return threadCopy.review.status.answered
    default:
      return status
  }
}

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
}
